"use client";

import { useState } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useTranslations } from "next-intl";
import { PageBackground } from "@/components/sections/page-background";
import { AutocompleteSearchBar } from "@/components/search/autocomplete-search-bar";
import { PageHeroWithSearch } from "@/components/sections/page-hero-with-search";
import { ReleaseNoteGrid } from "./release-note-grid";
import { SimplePagination } from "@/components/pagination/simple-pagination";
import { formatNumber } from "@/lib/utils/currency";
import { useReleaseNotes } from "../_api/use-release-note-queries";
import { ReleaseNoteCategory } from "@/openapi/generated";
import { DEFAULT_PAGE_SIZE } from "../util/release-note-constants";

export function ReleaseNotesPageContent() {
  const t = useTranslations("pages");
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();

  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") ?? "");

  const pageParam = searchParams.get("page");
  const initialPageIndex = pageParam ? parseInt(pageParam, 10) - 1 : 0;
  const [currentPage, setCurrentPage] = useState(initialPageIndex);

  const selectedCategoryId = searchParams.get("category") || undefined;
  const minImportance = searchParams.get("minImportance")
    ? parseInt(searchParams.get("minImportance")!)
    : undefined;
  const maxImportance = searchParams.get("maxImportance")
    ? parseInt(searchParams.get("maxImportance")!)
    : undefined;

  const { data, isLoading, error } = useReleaseNotes({
    page: currentPage,
    size: DEFAULT_PAGE_SIZE,
    category: selectedCategoryId as ReleaseNoteCategory,
    minImportance,
    maxImportance,
    search: searchTerm,
    sortBy: "publishedAt",
    sortDirection: "desc",
  });

  const handlePageChange = (next: number) => {
    const zeroBasedPage = next - 1;
    if (zeroBasedPage !== currentPage) {
      setCurrentPage(zeroBasedPage);
      const params = new URLSearchParams(searchParams.toString());
      params.set("page", next.toString());
      router.push(`${pathname}?${params.toString()}`);
    }
  };

  const handleSearch = (term: string) => {
    setCurrentPage(0);
    setSearchTerm(term);
    const params = new URLSearchParams(searchParams.toString());
    if (term) {
      params.set("search", term);
    } else {
      params.delete("search");
    }
    params.set("page", "1");
    router.push(`${pathname}?${params.toString()}`);
  };

  const content = data?.content || [];
  const totalPages = data?.totalPages ?? 0;
  const totalElements = data?.totalElements ?? 0;

  return (
    <PageBackground>
      <PageHeroWithSearch
        title={t("releaseNotes.title")}
        subtitle={t("releaseNotes.subtitle")}
      >
        <AutocompleteSearchBar
          sources={["releaseNotes"]}
          onSubmit={handleSearch}
          defaultValue={searchTerm}
          placeholder={t("releaseNotes.searchPlaceholder")}
        />
      </PageHeroWithSearch>

      <div className="max-w-7xl mx-auto space-y-6 pb-8">
        {error ? (
          <div className="text-center py-12">
            <p className="text-destructive">
              {t("releaseNotes.loadError")}
            </p>
          </div>
        ) : (
          <>
            {!isLoading && (
              <p className="text-xs text-muted-foreground text-right">
                {t("releaseNotes.resultsCount", { count: formatNumber(totalElements) })}
              </p>
            )}

            <ReleaseNoteGrid
              releaseNotes={content}
              loading={isLoading}
            />

            <SimplePagination
              currentPage={currentPage + 1}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </>
        )}
      </div>
    </PageBackground>
  );
}
